// Reporting dashboard — API client (Phase 1). Thin wrappers over the shared
// api() helper; filters go on the query string (arrays comma-separated, matching
// the server's reportingFiltersFromQuery parser).

import { api } from '../../lib/api';
import type {
  ReportingAttributionHealth,
  ReportingBackfill,
  ReportingBreakdownFilters,
  ReportingBreakdownResponse,
  ReportingCoverage,
  ReportingFilterOptions,
  ReportingFilters,
  ReportingPullMode,
  ReportingSummary,
  ReportingDemographicsFilters,
  ReportingDemographicsResponse,
  ReportingDataFreshness,
  ReportingDemographicsFreshness,
  ReportingSyncHealth,
} from '../../types/reporting';
import type { FxRateSnapshot } from '../../types/fxRates';

function toQueryString(filters: ReportingFilters): string {
  const p = new URLSearchParams();
  p.set('from', filters.from);
  p.set('to', filters.to);
  const add = (key: string, values?: string[]): void => {
    if (values && values.length > 0) p.set(key, values.join(','));
  };
  add('campaignTypes', filters.campaignTypes);
  add('brandAccountIds', filters.brandAccountIds);
  add('groupAccountIds', filters.groupAccountIds);
  add('industries', filters.industries);
  add('accountManagerIds', filters.accountManagerIds);
  add('countries', filters.countries);
  add('currencies', filters.currencies);
  add('accountIds', filters.accountIds);
  add('campaignSearchTokens', filters.campaignSearchTokens);
  // Per-level name tokens — comma-joined like every other array param. `adsetName`
  // keeps its wire name (the shared lib calls the middle level `adGroup`; on Meta
  // it is the AD SET).
  add('campaignName', filters.nameFilters?.campaignName);
  add('adsetName', filters.nameFilters?.adGroupName);
  add('adName', filters.nameFilters?.adName);
  return p.toString();
}

export function getReportingSummary(filters: ReportingFilters): Promise<ReportingSummary> {
  return api<ReportingSummary>(`/api/reporting/summary?${toQueryString(filters)}`);
}

export function getReportingFilterOptions(): Promise<ReportingFilterOptions> {
  return api<ReportingFilterOptions>('/api/reporting/filter-options');
}

// Phase 3a — the drill-down breakdown. Same filter serializer as the summary,
// plus the group-by (comma list) + optional parent-id scoping (3b lazy drill).
export function getReportingBreakdown(
  filters: ReportingBreakdownFilters,
): Promise<ReportingBreakdownResponse> {
  const p = new URLSearchParams(toQueryString(filters));
  p.set('groupBy', filters.groupBy.join(','));
  if (filters.campaignId) p.set('campaignId', filters.campaignId);
  if (filters.adsetId) p.set('adsetId', filters.adsetId);
  return api<ReportingBreakdownResponse>(`/api/reporting/breakdown?${p.toString()}`);
}

// §3b — the attribution-health indicators (date + currency + campaign search
// scoped only; the location/account filters don't apply to these rows).
export function getReportingAttributionHealth(f: {
  from: string;
  to: string;
  currencies?: string[];
  campaignSearchTokens?: string[];
}): Promise<ReportingAttributionHealth> {
  const p = new URLSearchParams();
  p.set('from', f.from);
  p.set('to', f.to);
  if (f.currencies && f.currencies.length > 0) p.set('currencies', f.currencies.join(','));
  if (f.campaignSearchTokens && f.campaignSearchTokens.length > 0) {
    p.set('campaignSearchTokens', f.campaignSearchTokens.join(','));
  }
  return api<ReportingAttributionHealth>(`/api/reporting/attribution-health?${p.toString()}`);
}

// The Meta AUDIENCE read (demographic breakdowns). ⚠️ accountId is REQUIRED and
// singular — each account has its own stored pull windows, so a multi-account
// scope would mix grains inside one figure. See src/lib/reporting/demographics.ts.
export function getReportingDemographics(
  filters: ReportingDemographicsFilters,
): Promise<ReportingDemographicsResponse> {
  const p = new URLSearchParams();
  p.set('accountId', filters.accountId);
  p.set('from', filters.from);
  p.set('to', filters.to);
  return api<ReportingDemographicsResponse>(`/api/reporting/demographics?${p.toString()}`);
}

// Data freshness — when the Meta pull last landed a fully-successful run. The
// LEAN, staff-visible slice of Sync Health (which is admin-only), and independent
// of the selected filters, so the dashboard fetches it once with a long staleTime.
export function getReportingDataFreshness(): Promise<ReportingDataFreshness> {
  return api<ReportingDataFreshness>('/api/reporting/data-freshness');
}

/**
 * When the NIGHTLY audience pass last landed a daily row, roster-wide.
 *
 * ⚠️ Distinct from `getReportingDataFreshness`, which describes the 2-hourly
 * DAILY pull. They are separate jobs on separate schedules, and on 2026-08-31
 * this one had been failing on every fire while that one stayed healthy.
 */
export function getReportingDemographicsFreshness(): Promise<ReportingDemographicsFreshness> {
  return api<ReportingDemographicsFreshness>('/api/reporting/demographics-freshness');
}

// Sync Health — recent pull-job runs + freshness signal (admin-only).
export function getReportingSyncHealth(): Promise<ReportingSyncHealth> {
  return api<ReportingSyncHealth>('/api/reporting/sync-health');
}

// Coverage — per-account × tier data-coverage map (admin-only ops view). With an
// accountId the response is scoped to that one account (the Meta Setup Analytics
// Coverage section); without it, the whole roster (the Coverage page).
export function getReportingCoverage(accountId?: string): Promise<ReportingCoverage> {
  const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
  return api<ReportingCoverage>(`/api/reporting/coverage${qs}`);
}

// ---- Build B: on-demand backfills (admin-only endpoints) ------------------

export function listBackfills(): Promise<{ backfills: ReportingBackfill[] }> {
  return api<{ backfills: ReportingBackfill[] }>('/api/reporting/backfills');
}

export function triggerBackfill(
  accountId: string,
  opts: { pullMode?: ReportingPullMode } = {},
): Promise<{ backfill: ReportingBackfill }> {
  return api<{ backfill: ReportingBackfill }>('/api/reporting/backfills', {
    method: 'POST',
    body: JSON.stringify({ accountId, ...(opts.pullMode ? { pullMode: opts.pullMode } : {}) }),
  });
}

export function retryBackfill(id: string): Promise<{ backfill: ReportingBackfill }> {
  return api<{ backfill: ReportingBackfill }>(`/api/reporting/backfills/${id}/retry`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ---- The agency display-currency rate snapshot -----------------------------
// ⚠️ ONE endpoint, read by BOTH dashboards (the Google page imports these from
// the reporting module's public surface). One rate table, one read — the two
// dashboards structurally cannot convert at different rates.

export function getFxRates(): Promise<{ rates: FxRateSnapshot | null }> {
  return api<{ rates: FxRateSnapshot | null }>('/api/reporting/fx-rates');
}

/** Admin: pull now. Reports failure in the body — the old rates still stand. */
export function refreshFxRates(): Promise<{
  ok: boolean;
  snapshot: FxRateSnapshot | null;
  error?: string;
}> {
  return api('/api/reporting/fx-rates/refresh', { method: 'POST' });
}

/** Admin: hand-edit an override when a pulled rate looks wrong. */
export function saveFxRates(input: {
  base?: string;
  month?: string;
  rates: Record<string, number>;
}): Promise<{ rates: FxRateSnapshot }> {
  return api('/api/reporting/fx-rates', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** Admin: refresh the Zernio profile cache (POST /api/zernio-profiles/sync). */
export type ZernioProfileSyncStats = {
  profilesFound: number;
  profilesNew: number;
  profilesUpdated: number;
  profilesDeleted: number;
};

export function syncZernioProfilesNow(): Promise<ZernioProfileSyncStats> {
  return api<ZernioProfileSyncStats>('/api/zernio-profiles/sync', { method: 'POST' });
}
